import React, { useState } from 'react';

import {
  ICustomRenderCellJSXProps,
  IListViewTableDataCell,
} from '~/src/dash/components/tables/ListViewTable/common';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  spacing,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EPlatformConnectOfferType,
  getOptions,
  IDealIDAndName,
  IPlatformConnectionDealExtRow,
  IPlatformConnectionUniqueDemandRow,
  IPlatformConnectListFilters,
  IPlatformConnectSelectedValue,
  IPlatformConnectUiParams,
  offerTypes,
} from '~/src/dash/views/platform-connect/common';
import { IOfferNewConfig } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { PlatformConnectDraftOfferNewContext } from '~/src/dash/views/platform-connect/DraftOfferNew/contexts';
import { DealExtension } from '~/src/dash/views/platform-connect/DraftOfferNew/DealExtension';
import { DealExtensionDrawer } from '~/src/dash/views/platform-connect/DraftOfferNew/DealExtensionDrawer';
import { UniqueDemand } from '~/src/dash/views/platform-connect/DraftOfferNew/UniqueDemand';
import { UniqueDemandDrawer } from '~/src/dash/views/platform-connect/DraftOfferNew/UniqueDemandDrawer';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

interface IDraftOfferNewProps {
  filters: IPlatformConnectListFilters;
  selectedValues: IPlatformConnectSelectedValue;
  dealExtensionOpportunitiesRows: IPlatformConnectionDealExtRow[];
  uniqueDemandOpportunitiesRows: IPlatformConnectionUniqueDemandRow[];
  fetchSellerPublishers: (params: IPlatformConnectUiParams) => void;
  fetchUniqueDemandOpportunities: (params: IPlatformConnectUiParams) => void;
  cfg: IOfferNewConfig;
  navigateToPlatformConnectsView: () => void;
  dealExtensionTotalRows: number;
  uniqueDemandTotalRows: number;
  dealExtLoadState: LoadingState;
  uniqueDemandLoadState: LoadingState;
  onChange: (key: string, value: string | number | string[] | IMenuList) => void;
  onAcceptDemandSource: (key: string) => void;
  handleClickOpenDrawer: (props: ICustomRenderCellJSXProps) => void;
  setDealExtensionOpportunitiesRows: React.Dispatch<
    React.SetStateAction<IPlatformConnectionDealExtRow[]>
  >;
  activeRowInfo: Record<string, IListViewTableDataCell>;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<any>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  deal: IDealIDAndName;
  disableDealExtOpportunities: boolean;
  disableUniqueDemandOpportunities: boolean;
}

export const PlatformConnectDraftOfferNewView = ({
  filters,
  selectedValues,
  dealExtensionOpportunitiesRows,
  uniqueDemandOpportunitiesRows,
  fetchSellerPublishers,
  fetchUniqueDemandOpportunities,
  cfg,
  navigateToPlatformConnectsView,
  dealExtensionTotalRows,
  uniqueDemandTotalRows,
  dealExtLoadState,
  uniqueDemandLoadState,
  onChange,
  onAcceptDemandSource,
  handleClickOpenDrawer,
  setDealExtensionOpportunitiesRows,
  activeRowInfo,
  isModalOpen,
  setIsModalOpen,
  error,
  setError,
  setSnackbarMessage,
  deal,
  disableDealExtOpportunities,
  disableUniqueDemandOpportunities,
}: IDraftOfferNewProps) => {
  const { session, user } = useBaseStore();

  const [offerType, setOfferType] = useState(
    String(EPlatformConnectOfferType.PLATFORM_CONNECTION_DEAL_EXTENSION),
  );

  return (
    <PlatformConnectDraftOfferNewContext.Provider
      value={{
        cfg,
        readAuthorization: IS_AUTHORIZED(['read_manage_seller_requests'], user.user),
        writeAuthorization: IS_AUTHORIZED(['write_manage_seller_requests'], user.user),
      }}
    >
      <Panels
        max
        vertical
        relative
        largeHorizontalPadding
        verticalPadding
        style={{ rowGap: spacing.medium }}
      >
        <Panel>
          <TopBar title='Platform Connect Beta' />
        </Panel>
        <Panel
          style={{
            padding: '0px 48px 16px 16px',
            borderBottom: `1px solid ${publicaColors.lightGray}`,
          }}
        >
          <Cols
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Col>
              <Text subheader>Draft New Offer</Text>
            </Col>
            <Col>
              <TextButton size='base' allCaps onClick={navigateToPlatformConnectsView}>
                Done
              </TextButton>
            </Col>
          </Cols>
        </Panel>
        <Panel>
          <Row noVerticalPadding>
            <Cols style={{ alignItems: 'center' }}>
              <Col
                style={{
                  minWidth: '200px',
                }}
              >
                Offer Type
              </Col>
              <Col>
                <Cols gap={spacing.extraSmall}>
                  <Col displayFlex>
                    <PillSwitcherV2
                      size='smallExtraLong'
                      options={getOptions(offerTypes, String(offerType))}
                      onChange={(value) => setOfferType(value)}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Row>
        </Panel>
        {offerType === String(EPlatformConnectOfferType.PLATFORM_CONNECTION_DEAL_EXTENSION) && (
          <>
            <DealExtension
              cfg={cfg}
              filters={filters}
              selectedValues={selectedValues}
              onChange={onChange}
              loadState={dealExtLoadState}
              dealExtensionOpportunitiesRows={dealExtensionOpportunitiesRows}
              fetchSellerPublishers={fetchSellerPublishers}
              totalRowCount={dealExtensionTotalRows}
              handleClickOpenDrawer={handleClickOpenDrawer}
              disableDealExtOpportunities={disableDealExtOpportunities}
            />
            <DealExtensionDrawer
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              publisher={session.publisher}
              deal={deal}
              impressionsGoal={Number(selectedValues.impressionsGoal)}
              activeRowInfo={activeRowInfo}
              setDealExtensionOpportunitiesRows={setDealExtensionOpportunitiesRows}
              error={error}
              setError={setError}
              setSnackbarMessage={setSnackbarMessage}
            />
          </>
        )}
        {offerType === String(EPlatformConnectOfferType.PLATFORM_CONNECTION_UNIQUE_DEMAND) && (
          <>
            <UniqueDemand
              cfg={cfg}
              filters={filters}
              onChange={onChange}
              onAcceptDemandSource={onAcceptDemandSource}
              loadState={uniqueDemandLoadState}
              uniqueDemandOpportunitiesRows={uniqueDemandOpportunitiesRows}
              fetchUniqueDemandOpportunities={fetchUniqueDemandOpportunities}
              totalRowCount={uniqueDemandTotalRows}
              handleClickOpenDrawer={handleClickOpenDrawer}
              disableUniqueDemandOpportunities={disableUniqueDemandOpportunities}
            />
            <UniqueDemandDrawer
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              publisher={session.publisher}
              activeRowInfo={activeRowInfo}
              onFetchUniqueDemandOpportunitiesList={fetchUniqueDemandOpportunities}
              error={error}
              setError={setError}
              setSnackbarMessage={setSnackbarMessage}
            />
          </>
        )}
        <PanelBreak height={spacing.large} />
      </Panels>
    </PlatformConnectDraftOfferNewContext.Provider>
  );
};
